import { Box, Stack, type SxProps, type Theme } from '@mui/material';
import WarningTriangleYellow from 'assets/images/warningTriangleYellow.svg';
import type { FC } from 'react';
import { ReactSVG } from 'react-svg';

interface WarningBannerProps {
  children?: React.ReactNode;
  wrapperSx?: SxProps<Theme>;
  icon?: string;
  isVisibleIcon?: boolean;
  iconPosition?: 'center' | 'top';
}

/**
 * A banner component that displays a warning message with a warning icon.
 * @param {React.ReactNode} [props.children] - The content to display inside the banner.
 * @param {SxProps<Theme>} [props.wrapperSx] - Additional styles to apply to the banner wrapper.
 * @param {string} [props.icon] - The icon to display in the banner, defaults to a warning circle icon.
 * @param {boolean} [props.isVisibleIcon=true] - Whether to display the icon or not. Defaults to true.
 */
const WarningBanner: FC<WarningBannerProps> = ({
  children,
  wrapperSx = [],
  icon = WarningTriangleYellow,
  isVisibleIcon = false,
  iconPosition = 'center',
}) => {
  return (
    <Box
      p={2}
      sx={[
        {
          backgroundColor: '#FFF7E6',
          borderColor: '#FFB300',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '8px',
          maxWidth: '980px',
        },
        ...(Array.isArray(wrapperSx) ? wrapperSx : [wrapperSx]),
      ]}
    >
      <Stack gap={(theme) => theme.spacing(1)} flexDirection="row" alignItems={'center'}>
        {isVisibleIcon && (
          <ReactSVG
            src={icon}
            aria-label={'Warning icon'}
            beforeInjection={(svg) => {
              svg.setAttribute('width', '20');
              svg.setAttribute('height', '20');
            }}
            style={{
              alignSelf: iconPosition === 'top' ? 'flex-start' : 'center',
            }}
          />
        )}
        {children}
      </Stack>
    </Box>
  );
};

export default WarningBanner;
